import React from "react";
import axios, { AxiosError } from "axios";
import {
  B2B_PORTAL_JWT_SECRET_KEY,
  B2B_PORTAL_CSRF_TOKEN,
  B2B_PORTAL_AUTH_REDIRECT_TO,
  B2B_PORTAL_UNAUTHORIZED_PATH,
} from "redmond";
import { useCorpRedirects, getStoredSessionToken, getStoredCsrfToken, USER_SOURCE_KEY, NONE } from "@capone/common";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";
import { createRequestLog, createResponseLog } from "../Logger";
import config from "../../utils/config";
import { UNPROTECTED_PATHS } from "../../utils/urlPaths";

interface IAxiosInterceptors {
  children?: React.ReactNode;
}

export const mcLeanAxiosInstance = axios.create?.();

const AxiosInterceptors = ({ children }: IAxiosInterceptors) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Keep the current location/navigate in refs so the single response
  // interceptor (registered once in the effect below) always reads the live
  // path instead of a stale closure from whichever render created it.
  const locationRef = React.useRef(location);
  const navigateRef = React.useRef(navigate);
  locationRef.current = location;
  navigateRef.current = navigate;

  useCorpRedirects(mcLeanAxiosInstance, config.TENANT);
  const getCookie = (name: string) => {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    if (match) return match[2];
    return undefined;
  };

  const getCsrfToken = () =>
    // Falls back to the login response's copy when the cookie is blocked, e.g. in a
    // third-party iframe (Brex). userInfo echoes this header back as `csrfToken`, which
    // the app uses as its logged-in signal.
    getCookie(B2B_PORTAL_CSRF_TOKEN) ?? getStoredCsrfToken();

  const setAuthRedirectUrl = (url: string) =>
    sessionStorage.setItem(B2B_PORTAL_AUTH_REDIRECT_TO, url);

  React.useEffect(() => {
    const responseError = (error: AxiosError) => {
      if (error.response?.status === 401) {
        const { pathname, search, hash } = locationRef.current;
        const isUnprotectedPath = UNPROTECTED_PATHS.some((path) => {
          const pathWithoutSlash = path.endsWith("/")
            ? path.slice(0, -1)
            : path;
          return pathname.startsWith(path) || pathname === pathWithoutSlash;
        });

        if (!isUnprotectedPath) {
          setAuthRedirectUrl(`${pathname}${search}${hash}`);
          navigateRef.current(B2B_PORTAL_UNAUTHORIZED_PATH);
        }
      }
      return Promise.reject(error);
    };
    const responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      responseError
    );

    const mcleanResponseInterceptor =
      mcLeanAxiosInstance.interceptors.response.use(
        (res) => {
          createResponseLog(res);
          return res;
        },
        (error) => Promise.reject(error)
      );

    const mcleanRequestInterceptor =
      mcLeanAxiosInstance.interceptors.request.use(
        (req) => {
          const csrfToken = getCsrfToken();
          const sessionToken = getStoredSessionToken();
          // First-login acquisition source for activation_channel capture. Mirrors the
          // @capone/portal interceptor: prefer the persisted user_source, fall back to the
          // live ?utm_source, and skip the "(none)" sentinel. Forwarded as X-User-Source so
          // the first authorized request reaches Tysons/URP with the channel; sanitized server-side.
          const userSource =
            sessionStorage.getItem(USER_SOURCE_KEY) ??
            new URLSearchParams(window.location.search).get("utm_source");

          // TODO: remove GCP token
          const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem(
              B2B_PORTAL_JWT_SECRET_KEY
            )}`,
            ...(csrfToken && { "H-Csrf-Token": csrfToken }),
            ...(sessionToken && { "Hopper-Session": sessionToken }),
            ...(userSource &&
              userSource !== NONE && { "X-User-Source": userSource }),
          };

          Object.assign(req.headers, headers);

          createRequestLog(req);
          return req;
        },
        (error) => Promise.reject(error)
      );

    // runs when component unmount
    return () => {
      mcLeanAxiosInstance.interceptors.request.eject(mcleanRequestInterceptor);
      mcLeanAxiosInstance.interceptors.response.eject(
        mcleanResponseInterceptor
      );
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return <>{children}</>;
};

export default AxiosInterceptors;
